// ============================================================
// src/Shared.js - FIXED & MODERNIZED (NO DESIGN CHANGES)
// ============================================================

import { useEffect, useState } from 'react';
import { createTheme, responsiveFontSizes } from '@material-ui/core/styles';
import { Color } from 'three/src/math/Color';

// ============================================================
// DESIGN TOKENS (still editable, but now used properly)
// ============================================================
export const primary   = '#6366f1';   // Indigo (main accent)
export const secondary = '#22d3ee';   // Cyan (highlight)
export const black     = '#0a0a0a';
export const white     = '#f8fafc';

// ============================================================
// BASE THEME (ONLY STATIC PARTS)
// ============================================================
const baseTheme = {
  typography: {
    fontFamily: '"DM Sans", system-ui, sans-serif',
    h1: { fontFamily: '"Space Mono", monospace', fontWeight: 700 },
    h2: { fontFamily: '"Space Mono", monospace', fontWeight: 700 },
    h3: { fontFamily: '"Space Mono", monospace', fontWeight: 700 },
  },
  shape: { borderRadius: 20 },
};

// ============================================================
// THEME FACTORY (FIXED)
// ============================================================
const getTheme = (mode) => {
  const isDark = mode === 'dark';

  return responsiveFontSizes(
    createTheme({
      ...baseTheme,

      palette: {
        type: mode,
        primary: { main: isDark ? '#6366f1' : '#4f46e5' },   // Indigo
        secondary: { main: '#22d3ee' },                      // Cyan

        background: {
          default: isDark ? '#050505' : '#f8fafc',   // Deeper black
          paper: isDark ? '#0f0f0f' : '#ffffff',
        },

        text: {
          primary: isDark ? '#f8fafc' : '#0f172a',
          secondary: isDark ? '#cbd5e1' : '#475569',
        },

        divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      },
    })
  );
};

export const getAppTheme = (mode) => getTheme(mode);

// ============================================================
// HOOKS
// ============================================================

export const useInViewport = (
  elementRef,
  unobserveOnIntersect = true,
  options = { threshold: 0.15 }
) => {
  const [intersect, setIntersect] = useState(false);

  useEffect(() => {
    const el = elementRef?.current;
    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIntersect(entry.isIntersecting);
      if (entry.isIntersecting && unobserveOnIntersect) {
        observer.unobserve(entry.target);
      }
    }, options);

    observer.observe(el);

    return () => observer.disconnect();
  }, [elementRef, unobserveOnIntersect, options]);

  return intersect;
};

export const usePrefersReducedMotion = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const reflow = (node) => node?.offsetHeight;


// ============================================================
// THREE.JS UTILITIES
// ============================================================

export const rgbToThreeColor = (rgb) =>
  new Color(...rgb.split(' ').map(v => Number(v) / 255));

export const cleanScene = (scene) => {
  scene.traverse(object => {
    if (!object.isMesh) return;
    object.geometry?.dispose();
    const mats = Array.isArray(object.material)
      ? object.material
      : [object.material];
    mats.forEach(m => m?.dispose?.());
  });
};

export const cleanRenderer = (renderer) => {
  renderer?.dispose?.();
  renderer?.forceContextLoss?.();
};

export const removeLights = (lights) => {
  lights?.forEach(light => light?.parent?.remove?.(light));
};

// ============================================================
// LEGACY UTILS
// ============================================================

export const media = {
  desktop: 1600,
  laptop: 1280,
  tablet: 1024,
  mobile: 696,
  mobileS: 320,
};

export const numToPx = (num) => `${num}px`;
export const pxToRem = (px) => `${px / 16}rem`;